import { isResource } from '@medplum/core'
import type {
  Composition,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedComposition } from '#src/models/compositions/composition.ts'
import {
  CompositionCategory,
  CompositionType,
} from '#src/models/compositions/constants.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now } from '#src/utils.ts'

// One practitioner's private notepad on one participant. Its own category keeps
// it out of every note search, which filters on `CompositionCategory.Note`.
export class PractitionerNotepad extends ExtendedComposition {
  static override isInstance(value: unknown): value is Composition {
    return (
      isResource(value) &&
      isOfType(value, 'Composition') &&
      hasCodeableConcept(
        value.category,
        CompositionCategory.PractitionerNotepad
      )
    )
  }

  constructor({
    participant,
    author,
    content = '',
  }: {
    participant: Reference<Patient>
    author: Reference<Practitioner>
    content?: string
  }) {
    super({
      title: 'Notepad',
      status: 'preliminary',
      date: now(),
      type: CompositionType.PractitionerNotepad,
      category: [CompositionCategory.PractitionerNotepad],
      subject: participant,
      author: [author],
    })
    this.content = content
  }

  static searchQuery({
    participant,
    author,
  }: {
    participant: Reference<Patient>
    author: Reference<Practitioner>
  }): QueryTypes {
    return {
      category: CompositionCategory.PractitionerNotepad,
      subject: participant,
      author,
      'status:not': 'entered-in-error',
    }
  }

  get participant(): Reference<Patient> {
    invariant(
      isOfType(this.resource.subject, 'Patient'),
      `Subject for ${this.reference.reference} must be a Patient`
    )
    return this.resource.subject
  }

  get author(): Reference<Practitioner> {
    const author = this.resource.author?.[0]
    invariant(
      isOfType(author, 'Practitioner'),
      `Author for ${this.reference.reference} must be a Practitioner`
    )
    return author
  }

  // Rich text editor HTML, as on `CompositionNote`.
  get content(): string {
    return this.resource.section?.[0]?.text?.div ?? ''
  }

  // FHIR requires a section to carry text, entries or sub-sections, so an
  // empty notepad has no section at all.
  set content(content: string) {
    this.resource.section = content
      ? [{ text: { div: content, status: 'additional' } }]
      : undefined
    this.resource.date = now()
  }
}
